import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useApplicationLocalStorage } from '../../application/application-hook';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectPlan } from '@platform/client';
import type {
  PlanOrgBrief,
  PlanProBrief,
  PlanSkillAlignment,
  PlanContactCard,
} from '@platform/client/schemas';
import { Button, Input } from '@platform/components';
import { formatDateStr } from '../../util/DateHelpers';
import { ProjectPlanFormSection as FormSection } from '../shared-components/ProjectPlanFormSection';
import { OriginalProjectDescription } from '../shared-components/OriginalProjectDescription';

type BeforeTheCallLocalData = {
  // Rep fields
  objective?: string;
  obstacle?: string;
  impact?: string;
  stepsTaken?: string;
  primaryOutcome?: string;
  constraints?: string;
  // Shared
  questions?: string;
  // Pro individual
  skillAlignment?: string;
  // Pro team
  skillAlignments?: Record<string, string>;
};

const SectionFooter = ({
  isCompleted,
  isSaving,
  isSubmitDisabled,
  savedDate,
  onEdit,
  onMarkComplete,
}: {
  isCompleted: boolean;
  isSaving: boolean;
  isSubmitDisabled: boolean;
  savedDate: string;
  onEdit?: () => void;
  onMarkComplete: () => void;
}) => (
  <div className='flex items-center gap-4'>
    {isCompleted ?
      <>
        <Button
          variant='secondary-gray'
          size='sm'
          leadIcon='edit-04'
          leadIconClassNames='stroke-gray-500'
          onPress={onEdit}
        >
          Edit
        </Button>
        <span className='text-xs font-normal leading-[18px] text-gray-400'>
          Last saved {savedDate}
        </span>
      </>
    : <>
        <Button
          variant='primary'
          size='sm'
          leadIcon='check'
          leadIconClassNames='stroke-white'
          onPress={onMarkComplete}
          isDisabled={isSubmitDisabled}
        >
          {isSaving ? 'Saving...' : 'Mark complete'}
        </Button>
        <span className='text-xs font-normal leading-[18px] text-gray-400'>
          All changes saved automatically
        </span>
      </>
    }
  </div>
);

// ─── Team view ────────────────────────────────────────────────────────────────

type TeamBeforeTheCallProps = {
  planId: number;
  initialProBrief?: PlanProBrief | null;
  initialSkillAlignments: PlanSkillAlignment[];
  teamMembers: PlanContactCard[];
  currentUserId?: number;
  isCompleted: boolean;
  savedAt?: string;
  projectDescription?: string | null;
  onComplete?: () => void;
  onEdit?: () => void;
};

type TeamForm = { questions: string };

const TeamBeforeTheCall = ({
  planId,
  initialProBrief,
  initialSkillAlignments,
  teamMembers,
  currentUserId,
  isCompleted,
  savedAt,
  projectDescription,
  onComplete,
  onEdit,
}: TeamBeforeTheCallProps) => {
  const [savedDate, setSavedDate] = useState(() =>
    savedAt ? formatDateStr(new Date(savedAt)) : '',
  );

  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<BeforeTheCallLocalData>(`project-plan-before-${planId}`);
  const [storedData] = useState(() => getApplicationData());

  const [skillAlignments, setSkillAlignments] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      initialSkillAlignments.map((s) => [
        s.userId,
        s.skillsAlignment || storedData.skillAlignments?.[String(s.userId)] || '',
      ]),
    ),
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<TeamForm>({
    defaultValues: { questions: initialProBrief?.questions || storedData.questions || '' },
  });

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      updateApplicationData(values as Partial<BeforeTheCallLocalData>);
    });
    return unsubscribe;
  }, [watch, updateApplicationData]);

  const queryClient = useQueryClient();
  const skillAlignmentMutation = ProjectPlan.useUpdateSkillAlignment();
  const proBriefMutation = ProjectPlan.useUpdateProBrief();

  const onSubmit = (data: TeamForm) => {
    const skillAlignmentsData = teamMembers
      .map((m) => ({
        userId: m.id,
        skillsAlignment: skillAlignments[m.id] ?? '',
      }))
      .filter((s) => s.skillsAlignment.trim() !== '');
    skillAlignmentMutation.mutate(
      {
        planId,
        data: {
          skillAlignments: skillAlignmentsData,
        },
      },
      {
        onSuccess() {
          proBriefMutation.mutate(
            { planId, data: { questions: data.questions } },
            {
              onSuccess() {
                reset(data);
                queryClient.invalidateQueries({
                  queryKey: ProjectPlan.getGetProjectPlanByIdQueryKey(planId),
                });
                setSavedDate(formatDateStr(new Date()));
                onComplete?.();
              },
            },
          );
        },
      },
    );
  };

  const isSaving = skillAlignmentMutation.isPending || proBriefMutation.isPending;
  const isSubmitDisabled = isSaving;

  const handleMarkComplete = () => {
    setSubmitAttempted(true);
    const currentUserFilled = !currentUserId || (skillAlignments[currentUserId] ?? '').trim();
    if (!currentUserFilled) return;
    handleSubmit(onSubmit)();
  };

  return (
    <div className='flex flex-col gap-6'>
      <OriginalProjectDescription description={projectDescription} />

      <FormSection
        title='How does your experience align with this project?'
        required
        description="Focus on experience or skills that are most relevant to the organization's goals or challenges."
        exampleContent="I've supported teams working to improve customer onboarding and early user activation, especially in resource-constrained environments. My experience includes identifying friction points, simplifying processes, and helping teams prioritize changes that have the biggest impact."
      >
        <div className='flex flex-col gap-6'>
          {teamMembers.map((member) => {
            const isCurrentUser = member.id === currentUserId;
            return (
              <div key={member.id} className='flex flex-col gap-1.5'>
                <span className='text-sm font-medium leading-5 text-gray-500'>
                  {member.firstname} {member.lastname}
                </span>
                <Input
                  type='multiline'
                  placeholder='e.g., I have experience in marketing and growth, including customer research, messaging, and improving onboarding and conversion flows.'
                  value={skillAlignments[member.id] ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setSkillAlignments((prev) => {
                      const next = { ...prev, [member.id]: e.target.value };
                      updateApplicationData({
                        skillAlignments: Object.fromEntries(
                          Object.entries(next).map(([k, v]) => [String(k), v]),
                        ),
                      });
                      return next;
                    });
                  }}
                  disabled={isCompleted || !isCurrentUser}
                  error={
                    submitAttempted && isCurrentUser && !(skillAlignments[member.id] ?? '').trim() ?
                      'This response is required'
                    : undefined
                  }
                  characterCount={isCurrentUser ? 400 : undefined}
                />
              </div>
            );
          })}
        </div>
      </FormSection>

      <FormSection
        title='What questions do you have for the organization?'
        description="List any remaining questions, clarifications, or topics you'd like to discuss on the planning call. Each team member can add their own questions on separate lines."
        exampleContent="I'd like to clarify onboarding priorities, any constraints we should plan around, and how success will be defined. I also want to understand what data is available and how feedback and decisions will be handled throughout the work."
      >
        <Controller
          name='questions'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              placeholder={`-Can we discuss the timeline for deliverables?\n-What does success look like for this project?\n-How will we communicate progress?\n-What's the budget for this initiative?`}
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <SectionFooter
        isCompleted={isCompleted}
        isSaving={isSaving}
        isSubmitDisabled={isSubmitDisabled}
        savedDate={savedDate}
        onEdit={onEdit}
        onMarkComplete={handleMarkComplete}
      />
    </div>
  );
};

// ─── Individual representative view ──────────────────────────────────────────

type IndividualBeforeTheCallProps = {
  planId: number;
  initialData?: PlanOrgBrief | null;
  isCompleted: boolean;
  savedAt?: string;
  projectDescription?: string | null;
  onComplete?: () => void;
  onEdit?: () => void;
};

type OrgBriefForm = {
  objective: string;
  obstacle: string;
  impact: string;
  stepsTaken: string;
  primaryOutcome: string;
  constraints: string;
  questions: string;
};

const IndividualBeforeTheCall = ({
  planId,
  initialData,
  isCompleted,
  savedAt,
  projectDescription,
  onComplete,
  onEdit,
}: IndividualBeforeTheCallProps) => {
  const [savedDate, setSavedDate] = useState(() =>
    savedAt ? formatDateStr(new Date(savedAt)) : '',
  );

  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<BeforeTheCallLocalData>(`project-plan-before-${planId}`);
  const [storedData] = useState(() => getApplicationData());

  const { control, handleSubmit, reset, watch } = useForm<OrgBriefForm>({
    defaultValues: {
      objective: initialData?.objective || storedData.objective || '',
      obstacle: initialData?.obstacle || storedData.obstacle || '',
      impact: initialData?.impact || storedData.impact || '',
      stepsTaken: initialData?.stepsTaken || storedData.stepsTaken || '',
      primaryOutcome: initialData?.primaryOutcome || storedData.primaryOutcome || '',
      constraints: initialData?.constraints || storedData.constraints || '',
      questions: initialData?.questions || storedData.questions || '',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      updateApplicationData(values as Partial<BeforeTheCallLocalData>);
    });
    return unsubscribe;
  }, [watch, updateApplicationData]);

  const queryClient = useQueryClient();
  const mutation = ProjectPlan.useUpdateOrganizationBrief();

  const onSubmit = (data: OrgBriefForm) => {
    mutation.mutate(
      { planId, data },
      {
        onSuccess() {
          reset(data);
          queryClient.invalidateQueries({
            queryKey: ProjectPlan.getGetProjectPlanByIdQueryKey(planId),
          });
          setSavedDate(formatDateStr(new Date()));
          onComplete?.();
        },
      },
    );
  };

  const isSaving = mutation.isPending;
  const isSubmitDisabled = isSaving;

  return (
    <div className='flex flex-col gap-6'>
      <OriginalProjectDescription description={projectDescription} />

      <FormSection
        title="Let's break down your challenge to scope the work"
        required
        description="You've already described your project above. Now, break it down using this format to help us scope the work more precisely."
        exampleContent={
          <div className='flex flex-col gap-1'>
            <p>
              <strong>We're trying to</strong> increase sales by improving our customer onboarding
              experience{' '}
            </p>
            <p>
              <strong>but we're stuck because</strong> our current onboarding process is manual and
              varies across teams{' '}
            </p>
            <p>
              <strong>and it's impacting us by</strong> slowing customer activation and reducing
              repeat purchases{' '}
            </p>
          </div>
        }
      >
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <span className='text-base font-medium leading-6 text-gray-500'>We're trying to</span>
            <Controller
              name='objective'
              control={control}
              rules={{ required: 'This response is required' }}
              render={({ field, fieldState }) => (
                <Input
                  type='inline'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='e.g., increase sales by improving our customer onboarding experience'
                  disabled={isCompleted}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <span className='text-base font-medium leading-6 text-gray-500'>
              but we're stuck because
            </span>
            <Controller
              name='obstacle'
              control={control}
              rules={{ required: 'This response is required' }}
              render={({ field, fieldState }) => (
                <Input
                  type='inline'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='e.g., our current onboarding process is manual and varies across teams'
                  disabled={isCompleted}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <span className='text-base font-medium leading-6 text-gray-500'>
              and it's impacting us by
            </span>
            <Controller
              name='impact'
              control={control}
              rules={{ required: 'This response is required' }}
              render={({ field, fieldState }) => (
                <Input
                  type='inline'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder='e.g., slowing customer activation and reducing repeat purchases'
                  disabled={isCompleted}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
      </FormSection>

      <FormSection
        title='What have you tried so far, and what do you already have?'
        description="Share any steps you've already taken to address this challenge, along with existing materials or information your professional(s) can build on. This helps avoid duplicating work."
        exampleContent="We've reviewed our current customer onboarding flow and mapped out the key steps from signup to first use. We identified a few points where customers tend to drop off, but we're not sure which issues matter most or how to prioritize fixes. We've had internal discussions about simplifying the process, but haven't tested any changes yet. We do have documentation of the current onboarding steps, notes from customer support conversations, screenshots of the existing flow, and basic usage data showing where customers disengage. We're looking for help turning this information into a clearer onboarding strategy and actionable improvements."
      >
        <Controller
          name='stepsTaken'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              value={field.value}
              onChange={field.onChange}
              placeholder="e.g., We've documented our current onboarding process and identified where customers get stuck. We have internal notes, support feedback, and examples of the current experience."
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <FormSection
        title="What's the single most important outcome you need?"
        required
        description="If tradeoffs are needed along the way, what's the top priority you want to make sure gets completed?"
        exampleContent="At minimum, we need a clearly defined and simplified onboarding flow that guides new customers from signup to their first successful action. Even if we don't redesign every screen or test multiple variations, we need a clear path that removes obvious friction and sets expectations for new users. Ideally, we'd also like to test different onboarding approaches, refine messaging, and measure improvements over time, but the most important outcome is a basic, effective onboarding experience we can implement and build on."
      >
        <Controller
          name='primaryOutcome'
          control={control}
          rules={{ required: 'This response is required' }}
          render={({ field, fieldState }) => (
            <Input
              type='multiline'
              value={field.value}
              onChange={field.onChange}
              placeholder='e.g., At minimum, we need a defined onboarding flow with clear steps from signup to first use.'
              disabled={isCompleted}
              error={fieldState.error?.message}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <FormSection
        title='Are there any constraints that could affect this work?'
        description='Like tool access, technology limitations, resources, timeline, etc.'
        exampleContent="We have limited engineering capacity, so any onboarding improvements need to work within our existing product and tools. We're currently using a basic analytics setup, so we don't have detailed user behavior tracking beyond high-level metrics. Our product and support teams are stretched, which limits how much internal feedback we can gather during the process. We also have a target timeline to improve onboarding before our next customer acquisition push, so we'll need to prioritize changes that can be implemented quickly and iterated on later."
      >
        <Controller
          name='constraints'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              value={field.value}
              onChange={field.onChange}
              placeholder='e.g., Limited engineering capacity, reliance on existing tools, and a tight timeline for launching improvements.'
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <FormSection title='What questions do you have for the professional(s)?'>
        <Controller
          name='questions'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              value={field.value}
              onChange={field.onChange}
              placeholder="List any remaining questions, assumptions, or topics you'd like to discuss on the planning call."
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <SectionFooter
        isCompleted={isCompleted}
        isSaving={isSaving}
        isSubmitDisabled={isSubmitDisabled}
        savedDate={savedDate}
        onEdit={onEdit}
        onMarkComplete={handleSubmit(onSubmit)}
      />
    </div>
  );
};

// ─── Individual professional view ─────────────────────────────────────────────

type ProIndividualBeforeTheCallProps = {
  planId: number;
  professionalId?: number;
  initialSkillAlignments: PlanSkillAlignment[];
  initialProBrief?: PlanProBrief | null;
  isCompleted: boolean;
  savedAt?: string;
  projectDescription?: string | null;
  onComplete?: () => void;
  onEdit?: () => void;
};

type ProIndividualForm = {
  skillAlignment: string;
  questions: string;
};

const ProIndividualBeforeTheCall = ({
  planId,
  professionalId,
  initialSkillAlignments,
  initialProBrief,
  isCompleted,
  savedAt,
  projectDescription,
  onComplete,
  onEdit,
}: ProIndividualBeforeTheCallProps) => {
  const [savedDate, setSavedDate] = useState(() =>
    savedAt ? formatDateStr(new Date(savedAt)) : '',
  );

  const { getApplicationData, updateApplicationData } =
    useApplicationLocalStorage<BeforeTheCallLocalData>(`project-plan-before-${planId}`);
  const [storedData] = useState(() => getApplicationData());

  const initialAlignment =
    initialSkillAlignments.find((s) => s.userId === professionalId)?.skillsAlignment ??
    initialSkillAlignments[0]?.skillsAlignment ??
    '';

  const { control, handleSubmit, reset, watch } = useForm<ProIndividualForm>({
    defaultValues: {
      skillAlignment: initialAlignment || storedData.skillAlignment || '',
      questions: initialProBrief?.questions || storedData.questions || '',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      updateApplicationData(values as Partial<BeforeTheCallLocalData>);
    });
    return unsubscribe;
  }, [watch, updateApplicationData]);

  const queryClient = useQueryClient();
  const skillAlignmentMutation = ProjectPlan.useUpdateSkillAlignment();
  const proBriefMutation = ProjectPlan.useUpdateProBrief();

  const onSubmit = (data: ProIndividualForm) => {
    const saveProBrief = () => {
      proBriefMutation.mutate(
        { planId, data: { questions: data.questions } },
        {
          onSuccess() {
            reset(data);
            queryClient.invalidateQueries({
              queryKey: ProjectPlan.getGetProjectPlanByIdQueryKey(planId),
            });
            setSavedDate(formatDateStr(new Date()));
            onComplete?.();
          },
        },
      );
    };

    if (professionalId) {
      skillAlignmentMutation.mutate(
        {
          planId,
          data: {
            skillAlignments: [{ userId: professionalId, skillsAlignment: data.skillAlignment }],
          },
        },
        { onSuccess: saveProBrief },
      );
    } else {
      saveProBrief();
    }
  };

  const isSaving = skillAlignmentMutation.isPending || proBriefMutation.isPending;
  const isSubmitDisabled = isSaving;

  return (
    <div className='flex flex-col gap-6'>
      <OriginalProjectDescription description={projectDescription} />

      <FormSection
        title='How does your experience align with this project?'
        required
        description="Focus on experience or skills that are most relevant to the organization's goals or challenges."
        exampleContent="I've supported teams working to improve customer onboarding and early user activation, especially in resource-constrained environments. My experience includes identifying friction points, simplifying processes, and helping teams prioritize changes that have the biggest impact."
      >
        <Controller
          name='skillAlignment'
          control={control}
          rules={{ required: 'This response is required' }}
          render={({ field, fieldState }) => (
            <Input
              type='multiline'
              placeholder='e.g., Experience improving customer onboarding, analyzing user drop-off, or supporting early-stage product teams.'
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              error={fieldState.error?.message}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <FormSection
        title='What questions do you have for the organization?'
        description="List any remaining questions, clarifications, or topics you'd like to discuss on the planning call."
        exampleContent="I'd like to clarify onboarding priorities, any constraints we should plan around, and how success will be defined. I also want to understand what data is available and how feedback and decisions will be handled throughout the work."
      >
        <Controller
          name='questions'
          control={control}
          render={({ field }) => (
            <Input
              type='multiline'
              placeholder='e.g., Which part of the onboarding experience should we prioritize first? What constraints should we keep in mind? How will success be defined?'
              value={field.value}
              onChange={field.onChange}
              disabled={isCompleted}
              characterCount={400}
            />
          )}
        />
      </FormSection>

      <SectionFooter
        isCompleted={isCompleted}
        isSaving={isSaving}
        isSubmitDisabled={isSubmitDisabled}
        savedDate={savedDate}
        onEdit={onEdit}
        onMarkComplete={handleSubmit(onSubmit)}
      />
    </div>
  );
};

// ─── Public export ────────────────────────────────────────────────────────────

type BeforeTheCallSectionProps = {
  planId: number;
  initialData?: PlanOrgBrief | null;
  initialProBrief?: PlanProBrief | null;
  savedAt?: string;
  isCompleted?: boolean;
  onComplete?: () => void;
  onEdit?: () => void;
  isTeam?: boolean;
  isProfessional?: boolean;
  professionalId?: number;
  currentUserId?: number;
  teamMembers?: PlanContactCard[];
  initialSkillAlignments?: PlanSkillAlignment[];
  projectDescription?: string | null;
};

export const BeforeTheCallSection = ({
  planId,
  initialData,
  initialProBrief,
  savedAt,
  isCompleted = false,
  onComplete,
  onEdit,
  isTeam = false,
  isProfessional = false,
  professionalId,
  currentUserId,
  teamMembers = [],
  initialSkillAlignments = [],
  projectDescription,
}: BeforeTheCallSectionProps) => {
  if (isTeam && isProfessional) {
    return (
      <TeamBeforeTheCall
        planId={planId}
        initialProBrief={initialProBrief}
        initialSkillAlignments={initialSkillAlignments}
        teamMembers={teamMembers}
        currentUserId={currentUserId}
        isCompleted={isCompleted}
        savedAt={savedAt}
        projectDescription={projectDescription}
        onComplete={onComplete}
        onEdit={onEdit}
      />
    );
  }

  if (!isTeam && isProfessional) {
    return (
      <ProIndividualBeforeTheCall
        planId={planId}
        professionalId={professionalId}
        initialSkillAlignments={initialSkillAlignments}
        initialProBrief={initialProBrief}
        isCompleted={isCompleted}
        savedAt={savedAt}
        projectDescription={projectDescription}
        onComplete={onComplete}
        onEdit={onEdit}
      />
    );
  }

  return (
    <IndividualBeforeTheCall
      planId={planId}
      initialData={initialData}
      isCompleted={isCompleted}
      savedAt={savedAt}
      projectDescription={projectDescription}
      onComplete={onComplete}
      onEdit={onEdit}
    />
  );
};
